import { useState } from "react";
import { ClipboardCheck, Search, Check, Minus, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { inputCls } from "./shared";

// Renders one day's 3 sessions as colored dots: green=trained, gray=rest/0-gain, hollow=pending.
function DayDots({ sessions }) {
  if (!sessions || sessions.length === 0) {
    return <span className="text-[11px] text-zinc-600 italic">rest day</span>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      {sessions.map((s, i) => {
        const trained = s.done && s.gain > 0;
        const restDone = s.done && s.gain <= 0;
        return (
          <span key={i} title={`Session ${s.session}: ${s.done ? (trained ? `+${s.gain} ${s.skill || ""}` : "no gain") : "pending"}`}
            className={`w-5 h-5 inline-flex items-center justify-center rounded-sm text-[10px] font-bold
              ${trained ? "bg-emerald-600/80 text-white" : restDone ? "bg-zinc-700 text-zinc-300" : "border border-zinc-700 text-zinc-600"}`}>
            {trained ? <Check size={11} /> : restDone ? <Minus size={11} /> : "·"}
          </span>
        );
      })}
    </span>
  );
}

export function TrainingAuditPanel() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setRows(null);
    try {
      const r = await api.get(`/admin/training-audit?q=${encodeURIComponent(q.trim())}&limit=300`);
      setRows(r.data.gladiators);
      if (!r.data.gladiators.length) toast.message("No gladiators matched.");
    } catch (e) { toast.error(apiErr(e)); setRows([]); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="training-audit-panel" className="mb-6 border border-amber-900/30 bg-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Training Audit — Last 2 Days</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-3">
        Verify training completed. Search by gladiator name or owner username (blank = all). Each gladiator is
        resolved live. <span className="text-emerald-400">●</span> trained · <span className="text-zinc-400">●</span> rest/no-gain ·
        <span className="text-zinc-600"> ○</span> pending. A finished day shows <span className="text-emerald-300">3/3</span>.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="training-audit-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="Gladiator name or owner username (e.g. watta)…" className={`${inputCls} pl-9`} />
        </div>
        <button data-testid="training-audit-run-btn" onClick={run} disabled={busy}
          className="px-5 py-2 bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-amber-400 disabled:opacity-50 rounded-sm">
          {busy ? "Loading…" : "Run Audit"}
        </button>
      </div>

      {rows !== null && rows.length > 0 && (
        <div className="border border-amber-900/20 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-900/60 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
              <th className="text-left px-3 py-2">Gladiator</th>
              <th className="text-left px-3 py-2 hidden sm:table-cell">Owner</th>
              <th className="text-right px-3 py-2 hidden md:table-cell">Age</th>
              <th className="text-center px-3 py-2">Yesterday</th>
              <th className="text-center px-3 py-2">Today</th>
              <th className="text-left px-3 py-2 hidden lg:table-cell">Last Resolved</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`training-audit-row-${r.id}`} className="border-b border-zinc-900">
                  <td className="px-3 py-2 text-zinc-100">{r.name}</td>
                  <td className="px-3 py-2 text-zinc-400 hidden sm:table-cell">{r.owner}</td>
                  <td className="px-3 py-2 text-right text-zinc-400 tabular-nums hidden md:table-cell">{r.age}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <DayDots sessions={r.yesterday} />
                      <span className={`text-[11px] tabular-nums ${(!r.yesterday?.length) ? "text-zinc-600" : r.yesterday_done >= 3 ? "text-emerald-400" : "text-amber-400"}`}>
                        {r.yesterday?.length ? `${r.yesterday_done}/3` : "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <DayDots sessions={r.today} />
                      <span className={`text-[11px] tabular-nums ${(!r.today?.length) ? "text-zinc-600" : "text-amber-400"}`}>
                        {r.today?.length ? `${r.today_done}/3` : "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-zinc-500 text-[11px] hidden lg:table-cell">
                    <span className="inline-flex items-center gap-1"><Clock size={11} />{r.last_tick ? new Date(r.last_tick).toLocaleString() : "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows !== null && rows.length === 0 && (
        <p data-testid="training-audit-empty" className="text-sm text-zinc-600 italic">No gladiators matched that search.</p>
      )}
    </div>
  );
}
